import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Text } from '@adobe/react-spectrum';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { CategoryChipBrowser } from '../../common/cardStats/CategoryChipBrowser';
import { FilterBanner } from '../../common/cardStats/FilterBanner';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { formatNumber, formatPct } from '../../common/cardStats/formatters';
import { ROLE_ORDER, roleColor } from '../../common/cardStats/roleColorMap';
import { getPrettyCategoryLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { useDeepLinkSelection } from '../../../utils/deepLink/useDeepLinkSegment';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';

/**
 * Sub-category ids we hide from the role breakdown. "combos" lives
 * under winConditions but overlaps with the Combos tab's own aggregate
 * view — backend will eventually drop it; until then filter here.
 */
const HIDDEN_SUB_CATEGORIES = new Set(['combos', 'combo']);



const RoleTile = ({ roleKey, data, deckCount }) => (
    <StatMetricTile
        label={getPrettyCategoryLabel(roleKey)}
        accentColor={roleColor(roleKey)}
        accentLabel
        value={formatNumber(data?.avgCardsPerDeck, 1)}
        valueUnit="avg cards / deck"
        footer={
            data?.avgScaledScore != null
                ? `avg score ${formatNumber(data.avgRawScore, 0)}`
                : null
        }
        title={`Mean distinct cards per deck filling at least one ${getPrettyCategoryLabel(
            roleKey
        ).toLowerCase()} sub-category, across ${deckCount} decks (${formatPct(
            data?.deckRate
        )} of decks hit this role)`}
    />
);

export function CardsTab({ bin, onSelectCard }) {
    // Selection matches the shared CategoryChipBrowser contract: null,
    // { kind: 'chip', key } for a single sub-category, or
    // { kind: 'group', key } for all subs in a role deduped. Mirrored
    // to the URL hash so filters survive reload / share.
    const [selection, setSelection] = useDeepLinkSelection('filter');

    const cards = bin?.cards || [];
    const categoryStats = bin?.cardCategoryStats;
    const roles = categoryStats?.roles || {};
    const subCategories = categoryStats?.subCategories || {};

    const roleGroups = useMemo(() => {
        return ROLE_ORDER.map((roleKey) => {
            const role = roles[roleKey];
            if (!role) return null;
            const subKeys = (role.subCategories || []).filter(
                (subKey) => subCategories[subKey] && !HIDDEN_SUB_CATEGORIES.has(subKey)
            );
            if (subKeys.length === 0) return null;
            return { roleKey, role, subKeys };
        }).filter(Boolean);
    }, [roles, subCategories]);

    // Reset selection if the bin swaps (e.g. bracket change). Skip the
    // first run so a deep-linked filter survives initial mount; the shared
    // CardStatList handles its own pagination + filter reset via resetKey.
    const prevBinRef = useRef(null);
    useEffect(() => {
        const currentBinId = `${bin?.binKey ?? ''}|${bin?.deckCount ?? ''}`;
        const firstRun = prevBinRef.current === null;
        prevBinRef.current = currentBinId;
        if (firstRun) return;
        setSelection(null);
    }, [bin?.binKey, bin?.deckCount, setSelection]);

    // Lookup from bin.cards so we can display the card's OVERALL count /
    // inclusionRate on filtered rows. topCards.count is per-classification
    // (e.g. "decks that scored Arcane Signet as ramp" = 1), which reads as
    // a bug to users since the card is in every deck — just usually scored
    // under Fast Mana or Mana Fixing instead.
    const cardsById = useMemo(() => {
        const map = new Map();
        cards.forEach((c) => map.set(c.cardId, c));
        return map;
    }, [cards]);

    // DFC back faces arrive in topCards keyed as `<front_id>_back` with
    // empty name/image, while bin.cards holds the merged front-face entry.
    // Fall back to the stripped id so the row renders with proper metadata
    // and the card-info dialog opens the right card.
    const hydrateTopCard = (tc) => {
        let overall = cardsById.get(tc.cardId);
        let resolvedId = tc.cardId;
        if (!overall || !tc.name) {
            const stripped = tc.cardId?.replace(/_back$/, '');
            if (stripped && stripped !== tc.cardId) {
                const front = cardsById.get(stripped);
                if (front) {
                    overall = front;
                    resolvedId = stripped;
                }
            }
        }
        if (!overall) return tc;
        return {
            ...tc,
            cardId: resolvedId,
            name: tc.name || overall.name,
            imageUri: tc.imageUri || overall.imageUri,
            types: tc.types || overall.types,
            count: overall.count,
            inclusionRate: overall.inclusionRate,
        };
    };

    // Build the filtered list based on the active selection. For a sub,
    // use its topCards directly. For a role, union the topCards across
    // every sub in that role, dedupe by cardId, and sort by overall
    // inclusion rate so the biggest-picture staples come first.
    const selectionData = useMemo(() => {
        if (!selection) return null;

        if (selection.kind === 'chip') {
            const sub = subCategories[selection.key];
            if (!sub) return null;
            const color = roleColor(sub.role);
            const hydrated = (sub.topCards || []).map(hydrateTopCard).filter((c) => c?.name);
            return {
                color,
                title: `Top ${getPrettyCategoryLabel(selection.key)} cards`,
                summary: `scored in ${sub.decksWithAny}/${bin.deckCount} decks · avg ${formatNumber(
                    sub.avgCardsPerDeck,
                    1
                )} cards / deck`,
                summaryTitle: `Decks where at least one card was scored specifically as ${getPrettyCategoryLabel(
                    selection.key
                ).toLowerCase()} (cards can fill multiple roles — a card like Arcane Signet might be scored as Fast Mana in most decks and Ramp in others).`,
                cards: hydrated,
            };
        }

        // Role selection — union topCards across every displayed sub.
        const group = roleGroups.find((g) => g.roleKey === selection.key);
        if (!group) return null;
        const role = group.role;
        const color = roleColor(selection.key);
        const seen = new Map();
        group.subKeys.forEach((subKey) => {
            const sub = subCategories[subKey];
            (sub?.topCards || []).forEach((tc) => {
                const hydrated = hydrateTopCard(tc);
                if (!hydrated?.name) return;
                if (seen.has(hydrated.cardId)) return;
                seen.set(hydrated.cardId, hydrated);
            });
        });
        const hydrated = Array.from(seen.values()).sort((a, b) => (b.inclusionRate || 0) - (a.inclusionRate || 0));
        return {
            color,
            title: `All ${getPrettyCategoryLabel(selection.key)} cards`,
            summary: `${hydrated.length} unique · avg ${formatNumber(role?.avgCardsPerDeck, 1)} / deck · scored in ${
                role?.decksWithAny || 0
            }/${bin.deckCount} decks`,
            summaryTitle: `Combined cards across every sub-category in ${getPrettyCategoryLabel(
                selection.key
            )}, deduped. Counts and percentages are each card's overall inclusion across the bin.`,
            cards: hydrated,
        };
    }, [selection, subCategories, cardsById, roleGroups, bin?.deckCount]);

    if (cards.length === 0 && !categoryStats) {
        return <Text UNSAFE_className="commanderCardStatsEmpty">No card data for this bucket yet.</Text>;
    }

    const listCards = selectionData ? selectionData.cards : cards;
    const resetKey = selectionData ? `${selection.kind}:${selection.key}:${bin?.binKey}` : `all:${bin?.binKey}`;
    const listEmptyMessage = selection ? 'No cards match these filters.' : 'No card data for this bucket yet.';
    const listEmptyMessageUnfiltered = selection
        ? 'No cards recorded for this filter.'
        : 'No card data for this bucket yet.';

    const clearSelection = () => {
        setSelection(null);
    };

    return (
        <Flex direction="column" rowGap={20} marginTop={10}>
            <HeroPanel
                tone="teal"
                eyebrow="Card Roster"
                title="Most-included cards"
                hint="Top cards in this cohort, broken down by role"
                aside={
                    <HeroPanelTotal
                        label="Decks"
                        value={bin?.deckCount?.toLocaleString() ?? '—'}
                    />
                }
            />
            {/* ----- 4-PANEL ROLE SUMMARY ----- */}
            {categoryStats && (
                <div className="statsMetricsGrid">
                    {ROLE_ORDER.map((roleKey) => (
                        <RoleTile key={roleKey} roleKey={roleKey} data={roles[roleKey]} deckCount={bin.deckCount} />
                    ))}
                </div>
            )}

            {/* ----- ROLE / SUB-CATEGORY CHIP BROWSER ----- */}
            {roleGroups.length > 0 && (
                <CategoryChipBrowser
                    title="Role breakdown"
                    analyticsContext="commander.cards"
                    subtitle="Click a role name to see every card in that role combined, or pick a sub-category for its top contributors"
                    groups={roleGroups.map((group) => {
                        const color = roleColor(group.roleKey);
                        return {
                            key: group.roleKey,
                            label: getPrettyCategoryLabel(group.roleKey),
                            color,
                            groupTitle: `Show every card across all ${getPrettyCategoryLabel(
                                group.roleKey
                            )} sub-categories, deduped`,
                            items: group.subKeys.map((subKey) => {
                                const sub = subCategories[subKey];
                                return {
                                    key: subKey,
                                    label: getPrettyCategoryLabel(subKey),
                                    count: formatNumber(sub?.avgCardsPerDeck, 1),
                                    title: `${sub?.decksWithAny || 0}/${bin.deckCount} decks (${formatPct(
                                        sub?.deckRate
                                    )}), avg ${formatNumber(sub?.avgCardsPerDeck, 1)} cards / deck`,
                                };
                            }),
                        };
                    })}
                    selection={selection}
                    onSelectionChange={(next) => setSelection(next)}
                />
            )}

            {/* ----- ACTIVE FILTER BANNER ----- */}
            {selectionData && (
                <FilterBanner
                    label="Showing"
                    value={selectionData.title}
                    detail={selectionData.summary}
                    detailTitle={selectionData.summaryTitle}
                    color={selectionData.color}
                    onClear={clearSelection}
                    ariaLabel="Clear role filter"
                />
            )}

            {/* ----- CARD LIST ----- */}
            <CardStatList
                cards={listCards}
                deckCount={bin.deckCount}
                onSelectCard={onSelectCard}
                resetKey={resetKey}
                emptyMessage={listEmptyMessage}
                emptyMessageUnfiltered={listEmptyMessageUnfiltered}
            />
        </Flex>
    );
}
